export interface StatCard {
  id: string;
  label: string;
  value: number | string;
  changeText: string;
  iconBgColor: string;
  iconColor: string;
  iconSvg: string;
  dataKey?: string; // API'den gelecek veri anahtarı
}

export interface ActivityItem {
  id: string;
  title: string;
  subtitle: string;
  date: string;
}

export interface MenuItem {
  id: string;
  label: string;
  labelEn?: string;
  path: string;
  icon: string;
  visible?: boolean;
  section?: string;
  sectionLabel?: string;
  sectionLabelEn?: string;
  sectionIcon?: string;
}

export interface PageConfig {
  // API endpoint'leri
  apiEndpoint?: string; // Örn: '/api/products'
  // Sayfa başlıkları ve metinler
  pageTitle?: string;
  addButtonLabel?: string;
  editButtonLabel?: string;
  deleteButtonLabel?: string;
  // Tablo sütunları (opsiyonel - varsayılan sütunlar kullanılır)
  columns?: Array<{
    key: string;
    label: string;
    visible?: boolean;
  }>;
  // Özel butonlar veya aksiyonlar
  customActions?: Array<{
    id: string;
    label: string;
    onClick: (item: any) => void;
    color?: string;
  }>;
}

export interface AppConfig {
  id: string;
  name: string;
  color: string;
  icon: string;
  headerTitle: string;
  headerSubtitle: string;
  stats: StatCard[];
  recentItemsTitle: string;
  recentItems: ActivityItem[];
  lowStockTitle?: string;
  lowStockItems?: ActivityItem[];
  activityLogsTitle?: string;
  customButtons?: Array<{
    id: string;
    label: string;
    path: string;
    color: string;
  }>;
  menuItems?: MenuItem[]; // Sidebar menü öğeleri
  pageTitles?: Record<string, string>; // Sayfa başlıkları (path -> title mapping)
  // Sayfa özelleştirmeleri
  pages?: {
    products?: PageConfig;
    categories?: PageConfig;
    [key: string]: PageConfig | undefined;
  };
}

export const appConfigs: Record<string, AppConfig> = {
  accounting: {
    id: 'accounting',
    name: 'Muhasebe',
    color: 'var(--status-active)',
    icon: 'FaFileInvoiceDollar',
    headerTitle: 'Muhasebe Yönetim Paneli',
    headerSubtitle: 'Finansal işlemler, faturalar ve raporlama',
    stats: [
      {
        id: 'invoices',
        label: 'Toplam Faturalar',
        value: 0,
        changeText: 'Bu ay',
        iconBgColor: 'var(--status-active-bg)',
        iconColor: 'var(--status-active)',
        iconSvg: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
        dataKey: 'totalInvoices'
      },
      {
        id: 'expenses',
        label: 'Toplam Giderler',
        value: 0,
        changeText: 'Bu ay',
        iconBgColor: 'var(--status-canceled-bg)',
        iconColor: 'var(--status-canceled)',
        iconSvg: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z',
        dataKey: 'totalExpenses'
      },
      {
        id: 'revenue',
        label: 'Gelir',
        value: 0,
        changeText: 'Bu ay',
        iconBgColor: 'var(--status-active-bg)',
        iconColor: 'var(--stat-icon-success)',
        iconSvg: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
        dataKey: 'totalRevenue'
      },
      {
        id: 'profit',
        label: 'Net Kar',
        value: 0,
        changeText: 'Gelir - Gider',
        iconBgColor: 'var(--status-expired-bg)',
        iconColor: 'var(--stat-icon-warning)',
        iconSvg: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
        dataKey: 'totalProfit'
      }
    ],
    recentItemsTitle: 'Son Faturalar',
    recentItems: [],
    customButtons: [
      { id: 'new-invoice', label: 'Yeni Fatura', path: '/app/accounting/invoices/new', color: 'var(--status-active)' },
      { id: 'new-expense', label: 'Yeni Gider', path: '/app/accounting/expenses/new', color: 'var(--status-canceled)' },
      { id: 'reports', label: 'Mali Raporlar', path: '/app/accounting/reports', color: 'var(--status-active)' }
    ],
    menuItems: [
      { id: 'dashboard', label: 'Muhasebe Paneli', labelEn: 'Accounting Dashboard', path: '/app/accounting', icon: 'dashboard', visible: true },
      { id: 'invoices', label: 'Faturalar', labelEn: 'Invoices', path: '/app/accounting/invoices', icon: 'product', visible: true },
      { id: 'expenses', label: 'Giderler', labelEn: 'Expenses', path: '/app/accounting/expenses', icon: 'product', visible: true },
      { id: 'reports', label: 'Mali Raporlar', labelEn: 'Financial Reports', path: '/app/accounting/reports', icon: 'dashboard', visible: true }
    ],
    pageTitles: {
      '/app/accounting': 'Muhasebe Paneli',
      '/app/accounting/invoices': 'Faturalar',
      '/app/accounting/expenses': 'Giderler',
      '/app/accounting/reports': 'Mali Raporlar',
      '/app/accounting/products': 'Ürünler',
      '/app/accounting/categories': 'Kategoriler',
      '/accounting': 'Gösterge Paneli',
      '/accounting/products': 'Ürünler',
      '/accounting/categories': 'Kategoriler'
    },
    pages: {
      products: {
        apiEndpoint: '/api/products', // Muhasebe'de farklı endpoint: '/api/accounting/products'
        pageTitle: 'Ürünler',
        addButtonLabel: 'Yeni Ürün Ekle',
        editButtonLabel: 'Ürünü Düzenle',
        deleteButtonLabel: 'Ürünü Sil'
      },
      categories: {
        apiEndpoint: '/api/categories',
        pageTitle: 'Kategoriler',
        addButtonLabel: 'Yeni Kategori Ekle',
        editButtonLabel: 'Kategoriyi Düzenle',
        deleteButtonLabel: 'Kategoriyi Sil'
      }
    }
  },
  // Varsayılan/Genel uygulama için (dashboard, products, categories gibi)
  default: {
    id: 'default',
    name: 'ERP',
    color: 'var(--text-secondary)',
    icon: 'FaChartArea',
    headerTitle: 'Gösterge Paneli',
    headerSubtitle: 'Güncel durum özetiniz',
    stats: [],
    recentItemsTitle: 'Son Eklenen Ürünler',
    recentItems: [],
    menuItems: [
      { id: 'dashboard', label: 'Gösterge Paneli', labelEn: 'Dashboard', path: '/dashboard', icon: 'dashboard', visible: true },
      { id: 'products', label: 'Ürünler', labelEn: 'Products', path: '/products', icon: 'product', visible: true },
      { id: 'categories', label: 'Kategoriler', labelEn: 'Categories', path: '/categories', icon: 'category', visible: true }
    ],
    pageTitles: {
      '/dashboard': 'Gösterge Paneli',
      '/products': 'Ürünler',
      '/categories': 'Kategoriler'
    }
  }
};
